import { LinearProgress, useTheme } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridInitialState,
  GridSlots,
  GridSortModel,
  GridCallbackDetails,
  useGridApiRef,
  GRID_CHECKBOX_SELECTION_FIELD,
} from '@mui/x-data-grid-pro';
import {
  useCallback,
  useLayoutEffect,
  useState,
  useMemo,
  useContext,
  useEffect,
  memo,
} from 'react';
import { useInfinitePaginationContext, useResourceContext } from 'react-admin';
import { useLocation } from 'react-router-dom';
import { isDeepEqual } from 'remeda';
import { useTranslation } from 'react-i18next';

import SkeletonBox from 'components/generic/SkeletonBox';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { defaultColumnsManagementSlotProps } from 'utility/dataGridDefaults';
import { getRoute } from 'utility/functions';
import { useNxtImport } from 'components/ExportImport/NxtImport/NxtImportContext';

//Wire/Syndication specific, not used in other contexts
import { WireSearchContext } from 'screens/syndication/views/search/syndication/WireSearchWithContext';

import { useSearch } from '../SearchContextProvider';
import { getGridColumnDefs, getGridInitialState } from '../gridColumnDefs';
import { getDatagridStylesInfo } from '../styles';
import { canSelectSearchItem } from '../utility/searchItemSelection';
import {
  GenericSearchItem,
  getRecordRealId,
  useSearchItems,
} from './SearchItemsProvider';
import { SearchGridCell, SearchGridCellProps } from './SearchGridCell';
import { SearchGridRow, SearchGridRowProps } from './SearchGridRow';
import {
  SearchGridBaseCheckbox,
  SearchGridBaseCheckboxProps,
} from './SearchGridCheckbox';
import classNames from 'classnames';
import { EditorialConfigData } from 'utility/types/dataProvider';

type GridInitialStatePro = NonNullable<DataGridProProps['initialState']>;

/**
 * Returns items from selectedItems that are NOT present in the current dataGridRows.
 * Used to preserve selections for rows hidden by filtering or pagination changes.
 */
export const getHiddenSelectedItems = (
  selectedItems: GenericSearchItem[],
  dataGridRows: any[],
  resource: string | undefined,
): GenericSearchItem[] => {
  const currentRowIds = new Set(
    dataGridRows.map((row) => getRecordRealId(row, resource)),
  );
  return selectedItems.filter(
    (item) => !currentRowIds.has(getRecordRealId(item, resource)),
  );
};

/**
 * Determine if we should clean sort/dir query params from the URL.
 *
 * Returns true ONLY when there IS something to remove — i.e. at least one of
 * sort/dir is present but they don't form a valid pair.
 */
export const shouldCleanSortQPs = (
  sort: string | undefined,
  dir: string | undefined,
): boolean => {
  const sortField = sort?.toLowerCase();
  const sortOrder = dir?.toLowerCase();
  const hasValidPair = Boolean(
    sortField && (sortOrder === 'asc' || sortOrder === 'desc'),
  );
  const hasAnySortParam = Boolean(sortField || sortOrder);
  return hasAnySortParam && !hasValidPair;
};

/**
 * Generic - search list view (all screens)
 *
 * All props should be drilled & optional as data
 * can come from various sources and contexts.
 */
export type SearchListViewProps = {
  rowData?: any[] | undefined;
  editorialConfig?: EditorialConfigData;
};

const SearchListView = memo(({ rowData, editorialConfig }: SearchListViewProps) => {
  const resource = useResourceContext();
  const theme = useTheme();
  const location = useLocation();
  const dataGridApiRef = useGridApiRef();
  const { hasSyndicationImports, imports } = useNxtImport();
  const { sessionConfig } = useCosmos();
  const { hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfinitePaginationContext();
  const { t } = useTranslation();
  const {
    view,
    updateQueryParams,
    isSavedSearch,
    accountType,
    subEnabled,
    validListFilter,
    getSearchItemInfo,
    currentRecordUuid,
    isModalSearch,
  } = useSearch();
  const {
    cleanData,
    recordMap,
    selectedItemIds,
    selectedItems,
    hasSelectionEnabled,
    hasMiniMenuEnabled,
    setSelectedItems,
    isFetching,
  } = useSearchItems();

  // Determine if this resource should use client-side sorting
  const [gridInitialState, setGridInitialState] = useState<GridInitialState | null>(null);
  const [dataGridRows, setDataGridRows] = useState<any[] | undefined>(undefined);
  const currentRoute = getRoute(location.pathname);

  const showMultiDomainCol = useMemo(() => {
    return resource === 'user/account/multisite';
  }, [resource]);

  // Get styles and row height info
  const memoizedDatagridStyleInfo = useMemo(
    () => getDatagridStylesInfo({ theme, view, resource }),
    [theme, view, resource],
  );

  const { sxStyles: dgStyles, rowHeight: cellHeight } = memoizedDatagridStyleInfo;

  const initialGridInitialState = useMemo<GridInitialStatePro>(
    () =>
      getGridInitialState(resource) ?? {
        preferencePanel: {
          open: false,
        },
      },
    [resource],
  );

  // Access Specific Contexts Safely
  const wireSearchContext = useContext(WireSearchContext);

  // Memoize column definitions
  const colDefs = useMemo(() => {
    return getGridColumnDefs({
      t: t, // pass t for reactive translation labels
      view: view,
      resource: resource,
      accountType: accountType,
      subEnabled: subEnabled,
      sessionConfig: sessionConfig,
      wireChannelOptions: wireSearchContext?.channelOptions,
      hasMiniMenuEnabled,
      showDomainColumn: showMultiDomainCol,
      editorialConfig: editorialConfig,
    });
  }, [
    t,
    resource,
    view,
    accountType,
    subEnabled,
    sessionConfig,
    hasMiniMenuEnabled,
    showMultiDomainCol,
    wireSearchContext?.channelOptions,
    editorialConfig,
  ]);

  // local storage key for gridstate, admin/normal views, we can add activeSite to the string for domain specificity if needed...?
  const gridStateStorageKey = useMemo(() => {
    const key = accountType
      ? `${currentRoute}-${view === 'summary' ? 'list' : view}-gridstate`
      : null;
    return key;
  }, [accountType, currentRoute, view]);

  // Helper to augment a grid state based on QPs and other various issues with MUI DG Pro.
  // See Note 1 in the file notes at EOF for more info on why this is needed.
  const augmentGridState = useCallback(
    (gridState: GridInitialStatePro, colDefs: any[]) => {
      if (
        !gridState ||
        typeof gridState !== 'object' ||
        !colDefs ||
        !Array.isArray(colDefs) ||
        colDefs.length === 0
      ) {
        console.error(
          '[SearchListView] Missing colDefs or gridState. Returning base state.',
          colDefs,
          gridState,
        );
        return initialGridInitialState; // Return the base state as a fallback.
      }

      const adjustedGridState = { ...gridState }; // Copy to avoid mutation
      const colDefsCopy = [...colDefs]; // Copy to avoid mutation

      // Handle sort/dir query params and set the sort model if they exist
      const sortField = validListFilter?.sort?.toLowerCase();
      const sortOrder = validListFilter?.dir?.toLowerCase();
      if (sortField && (sortOrder === 'asc' || sortOrder === 'desc')) {
        const sortModel: GridSortModel = [{ field: sortField, sort: sortOrder }];
        adjustedGridState.sorting = { sortModel: sortModel };
      } else {
        // Only call updateQueryParams if sort/dir were actually present in the URL.
        // (CMSADMIN-1524: KPI Bars) setSearchParams always triggers a navigate(), which removes
        // location.hash — even when the callback returns unchanged params.
        if (shouldCleanSortQPs(validListFilter?.sort, validListFilter?.dir)) {
          updateQueryParams((prevQueryParams) => {
            prevQueryParams.delete('sort');
            prevQueryParams.delete('dir');
            return prevQueryParams;
          });
        }
        adjustedGridState.sorting = { sortModel: [] }; // and set empty sort model
      }

      // Ensure all pinned columns are visible and adjust order if pinnedColumns and columns exist
      if (adjustedGridState.pinnedColumns && adjustedGridState.columns) {
        const pinnedColumns = adjustedGridState.pinnedColumns;
        const columns = adjustedGridState.columns;

        // Ensure columns and columnVisibilityModel exist
        if (!columns.columnVisibilityModel) {
          columns.columnVisibilityModel = {};
        }

        // Ensure all pinned columns are visible
        if (pinnedColumns.left) {
          pinnedColumns.left.forEach((field) => {
            if (field in columns.columnVisibilityModel!) {
              // Also, we need to use the TS non-null (!) assertion here, we know this path exists.
              columns.columnVisibilityModel![field] = true;
            }
          });
        }

        if (pinnedColumns.right) {
          pinnedColumns.right.forEach((field) => {
            if (field in columns.columnVisibilityModel!) {
              columns.columnVisibilityModel![field] = true;
            }
          });
        }

        // Ensure all columns exist in colDefs and handle undefined values in filtering
        const allColumns = colDefsCopy.map((col) => col.field);

        // Remove __check__ (GRID_CHECKBOX_SELECTION_FIELD) from all arrays to handle it separately
        const checkColumnInLeft = pinnedColumns.left?.includes(
          GRID_CHECKBOX_SELECTION_FIELD,
        );
        const checkColumnInRight = pinnedColumns.right?.includes(
          GRID_CHECKBOX_SELECTION_FIELD,
        );

        // Filter out __check__ from pinned arrays if it exists there
        if (checkColumnInLeft && pinnedColumns.left) {
          pinnedColumns.left = pinnedColumns.left.filter(
            (field) => field !== GRID_CHECKBOX_SELECTION_FIELD,
          );
        }
        if (checkColumnInRight && pinnedColumns.right) {
          pinnedColumns.right = pinnedColumns.right.filter(
            (field) => field !== GRID_CHECKBOX_SELECTION_FIELD,
          );
        }

        const middleColumns = allColumns.filter((field) => {
          // Exclude checkbox selection field
          if (field === GRID_CHECKBOX_SELECTION_FIELD) {
            return false;
          }
          // Exclude pinned columns (left or right)
          if (
            pinnedColumns.left?.includes(field) ||
            pinnedColumns.right?.includes(field)
          ) {
            return false;
          }

          return true;
        });

        // Set the order of columns with the
        // __check__ col always first in middle cols
        columns.orderedFields = [
          ...(pinnedColumns.left || []),
          GRID_CHECKBOX_SELECTION_FIELD,
          ...middleColumns,
          ...(pinnedColumns.right || []),
        ];

        // Ensure __check__ is visible
        if (GRID_CHECKBOX_SELECTION_FIELD in columns.columnVisibilityModel!) {
          columns.columnVisibilityModel![GRID_CHECKBOX_SELECTION_FIELD] = true;
        }
      }

      // Handle the preference panel, force it closed if it's open
      adjustedGridState.preferencePanel = { open: false };

      // Return the new state for chaining / any other setups
      return adjustedGridState;
    },
    [
      validListFilter?.sort,
      validListFilter?.dir,
      updateQueryParams,
      initialGridInitialState,
    ],
  );

  // Required to be able to restore column reorder via a state export
  // format the grid state coldefs to base coldef format before further further augmentation.
  const buildColDefsViaStateExport = useCallback(
    (exportedGridState: GridInitialStatePro, initialColDefs: any[]): any[] => {
      const orderedFields = exportedGridState.columns?.orderedFields ?? [];
      const dimensions = exportedGridState.columns?.dimensions ?? {};

      const colDefMap = new Map(initialColDefs.map((colDef) => [colDef.field, colDef]));

      const formattedColDefs = orderedFields.reduce<any[]>((acc, field) => {
        const baseColDef = colDefMap.get(field);

        // Handle known auto-added or conditionally excluded fields silently
        const isAllowedMissingField =
          field === GRID_CHECKBOX_SELECTION_FIELD || field === 'domain';

        if (!baseColDef) {
          if (!isAllowedMissingField) {
            // This is just a warning and functions fine without it, but it's good to know if it happens.
            console.warn(
              `[SearchListView] Missing column definition for field: ${field}`,
            );
          }
          // Skip adding anything to the accumulator
          return acc;
        }

        const dimension = dimensions[field] || {};
        acc.push({
          ...baseColDef,
          minWidth: dimension.minWidth ?? baseColDef.minWidth,
          maxWidth: dimension.maxWidth ?? baseColDef.maxWidth,
          width: dimension.width ?? baseColDef.width,
          flex: dimension.flex ?? baseColDef.flex,
        });

        return acc;
      }, []);

      return formattedColDefs;
    },

    [],
  );

  // Setup initial grid state based on query params and local storage
  useLayoutEffect(() => {
    // Only run if we have a storage key and no initial state, and augmentGridState needs colDefs.
    if (!gridStateStorageKey || gridInitialState !== null || !colDefs) {
      return;
    }
    const initializeGridStateSnapshot = async () => {
      const gridStateInStorage = localStorage.getItem(gridStateStorageKey);
      if (gridStateInStorage) {
        const parsedState = JSON.parse(gridStateInStorage);
        // Format colDefs based on the stored state
        const formattedColDefs = buildColDefsViaStateExport(parsedState, colDefs);
        const restoredGridState = augmentGridState(parsedState, formattedColDefs);
        setGridInitialState(restoredGridState);
      } else {
        const builtBaseState = augmentGridState(initialGridInitialState, colDefs);
        // Set the base state if nothing in storage
        setGridInitialState(builtBaseState);
      }
    };
    initializeGridStateSnapshot();
  }, [
    gridInitialState,
    colDefs,
    gridStateStorageKey,
    augmentGridState,
    buildColDefsViaStateExport,
    initialGridInitialState,
  ]);

  const saveGridStateHelper = useCallback(
    (details: GridCallbackDetails<any>) => {
      if (!gridStateStorageKey) {
        console.error('[SearchListView] Missing storage key:', gridStateStorageKey);
        return;
      }

      // Should never happen as we block the render, but just in case...
      if (!colDefs) {
        console.error('[SearchListView] Missing colDefs:', colDefs);
        return;
      }

      const gridExportedState = details?.api?.exportState();
      if (!gridExportedState) {
        return;
      }
      // Fallback to initial colDefs if required.
      const formattedColDefs = buildColDefsViaStateExport(gridExportedState, colDefs);

      // Run the true MUI DG State export through our augmentGridState helper
      const currentModifiedGridState = augmentGridState(
        gridExportedState,
        formattedColDefs,
      );

      if (currentModifiedGridState) {
        try {
          const currentStorageState = localStorage.getItem(gridStateStorageKey);
          const parsedStorageState = currentStorageState
            ? JSON.parse(currentStorageState)
            : null;
          // Update the local storage if the state differs
          if (!isDeepEqual(currentModifiedGridState, parsedStorageState)) {
            localStorage.setItem(
              gridStateStorageKey,
              JSON.stringify(currentModifiedGridState),
            );
          }
        } catch (error) {
          // If there's an error, revert to the base state and log the error
          console.error('[SearchListView] Grid state error - reverting to base:', error);
          // Try to get the base state / initialized again.
          const newGridState = augmentGridState(initialGridInitialState, colDefs);
          // Save the base state to storage
          localStorage.setItem(gridStateStorageKey, JSON.stringify(newGridState));
          // Also restore fallback state to the grid
          details.api?.restoreState(newGridState);
        }
      } else {
        // If somehow the apiref failed to export the state, log an error
        console.error('[SearchListView] State export failed:', currentModifiedGridState);
      }
    },
    [
      gridStateStorageKey,
      colDefs,
      buildColDefsViaStateExport,
      augmentGridState,
      initialGridInitialState,
    ],
  );

  const handleSortModelChange: NonNullable<DataGridProProps['onSortModelChange']> =
    useCallback(
      (model, details) => {
        // MUI Datagrid allows for some multisort stuff but we only want single sort hence the [0] access
        updateQueryParams((prevQueryParams) => {
          const sortField = model[0]?.field || '';
          const sortOrder = model[0]?.sort?.toUpperCase() || '';
          if (sortField && sortOrder) {
            prevQueryParams.set('sort', sortField);
            prevQueryParams.set('dir', sortOrder);
          } else {
            prevQueryParams.delete('sort');
            prevQueryParams.delete('dir');
          }
          return prevQueryParams;
        });
      },
      [updateQueryParams],
    );

  const handleColVisModelChange: NonNullable<
    DataGridProProps['onColumnVisibilityModelChange']
  > = useCallback(
    (model, details) => {
      saveGridStateHelper(details);
    },
    [saveGridStateHelper],
  );

  const handleColWidthChange: NonNullable<DataGridProProps['onColumnWidthChange']> =
    useCallback(
      (params, event, details) => {
        saveGridStateHelper(details);
      },
      [saveGridStateHelper],
    );

  const handleColOrderChange: NonNullable<DataGridProProps['onColumnOrderChange']> =
    useCallback(
      (params, event, details) => {
        saveGridStateHelper(details);
      },
      [saveGridStateHelper],
    );

  const handlePinnedColsChange: NonNullable<DataGridProProps['onPinnedColumnsChange']> =
    useCallback(
      (pinnedColumns, details) => {
        saveGridStateHelper(details);
      },
      [saveGridStateHelper],
    );

  const handleOnRowsScrollEnd = useCallback<
    NonNullable<DataGridProProps['onRowsScrollEnd']>
  >(async () => {
    if (!hasNextPage || isFetchingNextPage || isFetching) {
      return;
    }

    // Fetch next page. Cursor-based resources use direct useInfiniteQuery with proper getNextPageParam,
    // so cursor strings flow through automatically. Numeric resources use standard pagination.
    await fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFetching]);

  const handleGetRowHeight: NonNullable<DataGridProProps['getRowHeight']> = useCallback(
    (params) => {
      /**
       * Return calculated cell height (number/'auto')
       * based on view and resource style helper.
       */
      return cellHeight;
    },
    [cellHeight],
  );

  const handleGetRowClassName: NonNullable<DataGridProProps['getRowClassName']> =
    useCallback(
      (params) => {
        const { row, indexRelativeToCurrentPage } = params;

        // get item info via helper
        const info = getSearchItemInfo(row);
        const isDisabled = info?.disabled ?? false;

        // Check if this row is the current record being edited (modal search)
        const isCurrentRecord = currentRecordUuid && row?.uuid === currentRecordUuid;

        // zebra striping, disabled...
        const classes = classNames({
          even: indexRelativeToCurrentPage % 2 === 0,
          odd: indexRelativeToCurrentPage % 2 !== 0,
          disabled: isDisabled || isCurrentRecord,
        });

        return classes;
      },
      [getSearchItemInfo, currentRecordUuid],
    );

  const handleIsRowSelectable: NonNullable<DataGridProProps['isRowSelectable']> =
    useCallback(
      (params) => {
        // First check if selection is enabled at all
        if (!hasSelectionEnabled) {
          return false;
        }

        // Check if the row is disabled via getSearchItemInfo (only applies in modal mode)
        const searchItemInfo = getSearchItemInfo(params.row);
        if (!canSelectSearchItem(searchItemInfo, isModalSearch)) {
          return false;
        }

        return true;
      },
      [hasSelectionEnabled, getSearchItemInfo, isModalSearch],
    );

  // IMPORTANT: MUI DataGrid WILL INVOKE this function on-mount/rerender
  // and you cannot control when it will be called, they do this so you can
  // 'restore selections' on mount/reload, but that is not currently for our use case
  // so even if nothing is selected, MUI it will invoke with empty [] on mount...
  // meaning we must guard the setState call to prevent infinite loops
  // recommended: conlog and watch for inf rerender loops if modifying this fn.
  // console.debug('[render] PerfNow:', performance.now());
  const handleRowSelectModel: NonNullable<DataGridProProps['onRowSelectionModelChange']> =
    useCallback(
      (model, details) => {
        // MUI Datagrid model v8: { type: 'include' | 'exclude', ids: Set<GridRowId> }
        // so need to get the records based on the selection

        // no update if both old & new are empty
        const bothEmpty = model.ids.size === 0 && selectedItemIds.ids.size === 0;
        if (bothEmpty) {
          return;
        }

        // no update if models are deeply equal (compare type and ids)
        const unchanged =
          model.type === selectedItemIds.type &&
          model.ids.size === selectedItemIds.ids.size &&
          Array.from(model.ids).every((id) => selectedItemIds.ids.has(id));
        if (unchanged) {
          return;
        }

        // Preserve selected items not in the current dataGridRows
        // (e.g. hidden because the API returned different results or a client-side filter is active)
        const hiddenSelected = getHiddenSelectedItems(
          selectedItems as GenericSearchItem[],
          dataGridRows || [],
          resource,
        );

        // Get selected records based on the model type
        const newSelected: GenericSearchItem[] = [];
        if (model.type === 'include') {
          // For include mode, iterate over the selected IDs
          for (const id of model.ids) {
            const rec = recordMap.get(String(id)); // alt: details.api.getRow(id)
            if (rec) {
              // Check if the item is disabled before adding to selection (only applies in modal mode)
              const searchItemInfo = getSearchItemInfo(rec);
              if (!canSelectSearchItem(searchItemInfo, isModalSearch)) {
                continue; // Skip disabled items
              }
              newSelected.push(rec);
            } else {
              console.warn(`[handleRowSelectModel] No map data for ID: ${id}`);
            }
          }
        } else {
          // For exclude mode, select all rows NOT in the exclusion set
          for (const [id, rec] of recordMap.entries()) {
            if (!model.ids.has(id)) {
              const searchItemInfo = getSearchItemInfo(rec);
              if (canSelectSearchItem(searchItemInfo, isModalSearch)) {
                newSelected.push(rec);
              }
            }
          }
        }

        // Batch update state: merge hidden selections with newly selected visible rows
        setSelectedItems([...hiddenSelected, ...newSelected]);
      },
      [
        selectedItemIds,
        selectedItems,
        recordMap,
        dataGridRows,
        resource,
        setSelectedItems,
        getSearchItemInfo,
        isModalSearch,
      ],
    );

  // note on gridslots, cannot do something eg:
  // cell: (muiCellProps) =>( <SearchGridCell {...muiCellProps} customProps={...}/> )
  // as datagrid will complain about fwdRef, though this doesnt seem to cause
  // any real issues, we will need to specify the function (not jsx) in the gridSlots
  // and then pass our custom props down via slotProps, which is a bit annoying.
  const gridSlots: Partial<DataGridProProps['slots']> = useMemo(() => {
    return {
      cell: SearchGridCell,
      row: SearchGridRow,
      // per MUI baseCheckbox is used for ALL checkboxes within the datagrid
      // row, header, column selector, etc... more notes inside the component
      baseCheckbox: SearchGridBaseCheckbox as GridSlots['baseCheckbox'],
      loadingOverlay: LinearProgress as GridSlots['loadingOverlay'],
    };
  }, []);

  // pass or override props to gridslots
  const gridSlotProps: Partial<DataGridProProps['slotProps']> = useMemo(() => {
    return {
      cell: {
        customProps: {},
      } as Partial<SearchGridCellProps>,
      row: {
        customProps: {},
      } as Partial<SearchGridRowProps>,
      baseCheckbox: {
        customProps: {},
      } as Partial<SearchGridBaseCheckboxProps>,
      columnsManagement: defaultColumnsManagementSlotProps,
      // `placement` was removed from GridPanelProps in MUI X v8; cast to preserve runtime behaviour
      // To maintain our existing display
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      panel: { placement: 'bottom-start' } as any,
    };
  }, []);

  const gridLocaleText: Partial<DataGridProProps['localeText']> = useMemo(() => {
    return {
      noRowsLabel: 'No results found.',
    };
  }, []);

  // Load rows
  useEffect(() => {
    if (!isFetching && !isFetchingNextPage) {
      const data = rowData || cleanData;
      setDataGridRows(data);
    }
  }, [rowData, cleanData, isFetching, isFetchingNextPage, resource]);

  // Wire specific
  useEffect(() => {
    if (!isFetching && !isFetchingNextPage && hasSyndicationImports) {
      if (resource === 'wire/channel/search' && hasSyndicationImports) {
        const currentChannel = wireSearchContext?.selectedChannel;
        if (currentChannel) {
          const importsForChannel = Object.values(imports)
            .filter((e) => e.resourceType === 'wire')
            .filter((imp) => imp.importId.startsWith(currentChannel));
          if (importsForChannel?.length > 0) {
            // Ensure dataGridRows is defined
            setDataGridRows((prevRows) => {
              const updatedRows = [...(prevRows || [])];
              importsForChannel.forEach((importStatus) => {
                const importIdParts = importStatus.importId.split(':');
                const object_id = importIdParts.slice(1).join(':');
                const rowIndex = updatedRows.findIndex(
                  (row) => row.object_id === object_id,
                );
                if (rowIndex !== -1 && rowIndex !== undefined) {
                  let status = null;
                  if (
                    importStatus.isProcessing ||
                    importStatus.isQueued ||
                    importStatus.isUploading
                  ) {
                    status = 'importing';
                  } else if (importStatus.isFailed) {
                    status = 'import_error';
                  } else if (importStatus.isReady) {
                    status = 'imported';
                  } else {
                    status = null; // fallback to whatever we have
                  }
                  updatedRows[rowIndex] = {
                    ...updatedRows[rowIndex], // Keep other properties intact
                    status: status,
                    asset_uuid: importStatus.uuid,
                  };
                }
              });

              return updatedRows; // Only update the state if there were changes
            });
          }
        }
      }
    }
  }, [
    isFetching,
    isFetchingNextPage,
    hasSyndicationImports,
    resource,
    wireSearchContext,
    setDataGridRows,
    imports,
  ]);

  if (
    !dataGridRows ||
    !colDefs ||
    !gridInitialState ||
    !gridStateStorageKey ||
    !dataGridApiRef
  ) {
    return (
      <SkeletonBox
        text="Loading..."
        width="100%"
        height="100%"
      />
    );
  }

  return (
    <DataGridPro
      data-testid="search-results-container"
      apiRef={dataGridApiRef}
      columns={colDefs}
      rows={dataGridRows}
      localeText={gridLocaleText}
      slots={gridSlots}
      slotProps={gridSlotProps}
      density="compact"
      initialState={gridInitialState}
      loading={isFetchingNextPage}
      scrollEndThreshold={300}
      sortingMode="server"
      filterMode="server"
      rowsLoadingMode="client"
      getRowHeight={handleGetRowHeight}
      getRowClassName={handleGetRowClassName}
      rowSelectionModel={selectedItemIds}
      keepNonExistentRowsSelected
      onRowSelectionModelChange={handleRowSelectModel}
      rowSelection={hasSelectionEnabled}
      checkboxSelection={hasSelectionEnabled}
      isRowSelectable={handleIsRowSelectable}
      onRowsScrollEnd={handleOnRowsScrollEnd}
      onSortModelChange={handleSortModelChange}
      onColumnVisibilityModelChange={handleColVisModelChange}
      onColumnWidthChange={handleColWidthChange}
      onColumnOrderChange={handleColOrderChange}
      onPinnedColumnsChange={handlePinnedColsChange}
      disableColumnFilter={true}
      disableVirtualization={false}
      disableRowSelectionOnClick={true}
      disableDensitySelector={true}
      disableMultipleColumnsSorting={true}
      disableMultipleColumnsFiltering={true}
      hideFooter={true}
      sx={dgStyles}
      // SavedSearchGrid specific
      disableColumnSelector={isSavedSearch}
      disableColumnPinning={isSavedSearch}
      disableColumnSorting={isSavedSearch} // off until api supports this
      disableColumnMenu={isSavedSearch} // no options at the moment
    />
  );
});
SearchListView.displayName = 'SearchListView';
export default SearchListView;
